import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import Sheet from "components/sheet/Sheet";
import Input from "components/form/input/Input";
import Radio from "components/radio";
import { ButtonNew as Button } from "components/buttonNew";
import { LocaleCurrency, LocaleCurrencyString } from "helpers/Locale";
import useLocalStorage from "hooks/useLocalStorage";
import { IRegisterGroup } from "pages/secure/facility/Admin";

interface ICashTipModalProps {
  open: boolean;
  currentTip: number;
  currency?: string;
  okLoading?: boolean;
  onCancel: () => void;
  onOk: (tipAmount: number) => void;
}

export default function CashTipModal(props: ICashTipModalProps) {
  const { t } = useTranslation();
  const registerGroup = useLocalStorage<IRegisterGroup>("register_group");
  const isAdjust = Number(props.currentTip) > 0;

  const [tipAmount, setTipAmount] = useState("0.00");
  const [selectedQuickAmount, setSelectedQuickAmount] = useState<number | null>(null);

  useEffect(() => {
    if (props.open) {
      return;
    }

    // Reset while closed so the next open does not flash the previous selection.
    const amount = Number(props.currentTip) > 0 ? Number(props.currentTip).toFixed(2) : "0.00";
    setTipAmount(amount);
    setSelectedQuickAmount(null);
  }, [props.open, props.currentTip]);

  const quickSelectAmounts =
    registerGroup?.tipping_type === "fixed_amount"
      ? [
          registerGroup.tip_fixed_amount_low,
          registerGroup.tip_fixed_amount_mid,
          registerGroup.tip_fixed_amount_high,
        ].filter((amount): amount is number => amount != null)
      : [5, 10, 15];

  function handleTipChange(e: React.ChangeEvent<HTMLInputElement>) {
    const value = e.target.value;

    if (value === "") {
      setTipAmount("");
      setSelectedQuickAmount(null);
      return;
    }

    const numericValue = Number(value);
    if (!Number.isNaN(numericValue) && numericValue >= 0) {
      setTipAmount(value);
      setSelectedQuickAmount(null);
    }
  }

  function handleQuickSelect(value: string | number | Record<string, any>) {
    const amount = typeof value === "number" ? value : Number(value);
    if (Number.isNaN(amount)) {
      return;
    }

    setTipAmount(Number(amount).toFixed(2));
    setSelectedQuickAmount(amount);
  }

  function handleClearAll() {
    setTipAmount("0.00");
    setSelectedQuickAmount(null);
  }

  function handleOk() {
    const amount = Math.max(0, Number(tipAmount) || 0);
    props.onOk(Math.round(amount * 100) / 100);
  }

  const supportText = isAdjust
    ? t("Original tip of {{amount}} will be adjusted.", {
        amount: LocaleCurrencyString({
          currency: props.currency ?? "cad",
          amount: props.currentTip,
        }),
      })
    : t("Enter the tip collected for this order to add it to your records.");

  return (
    <Sheet
      title={isAdjust ? t("Adjust cash tip") : t("Add cash tip")}
      supportText={supportText}
      open={props.open}
      size="small"
      closable
      onCancel={props.onCancel}
      footer={null}
      secondaryFooter={
        <div className="ui-sheet-footer" style={{ margin: "16px -24px 0", paddingTop: "24px" }}>
          <div className="ui-sheet-footer-action">
            <Button type="text" className="text-md text-semibold text-gray-900" onClick={handleClearAll}>
              {t("Clear all")}
            </Button>
          </div>
          <div className="ui-sheet-footer-actions">
            <div className="ui-sheet-footer-action">
              <Button type="default" onClick={props.onCancel}>
                {t("Cancel")}
              </Button>
            </div>
            <div className="ui-sheet-footer-action">
              <Button type="primary" onClick={handleOk} loading={props.okLoading}>
                {isAdjust ? t("Update changes") : t("Add Tip")}
              </Button>
            </div>
          </div>
        </div>
      }
    >
      <Input
        label={t("Enter amount")}
        type="number"
        min={0}
        step="0.01"
        prefix="$"
        value={tipAmount}
        autoFocus
        onFocus={(e: React.ChangeEvent<HTMLInputElement>) => e.target.select()}
        onChange={handleTipChange}
      />

      {quickSelectAmounts.length > 0 ? (
        <div className="mt-4 mb-4">
          <p className="text-sm text-semibold mb-2">{t("Quick select")}</p>
          <Radio.Group name="cash-tip-quick-select" onChange={handleQuickSelect} value={selectedQuickAmount}>
            {quickSelectAmounts.map(amount => (
              <Radio.Button key={amount} value={amount}>
                <LocaleCurrency currency={props.currency ?? "cad"} amount={amount} />
              </Radio.Button>
            ))}
          </Radio.Group>
        </div>
      ) : null}
    </Sheet>
  );
}
